import { useState, useMemo } from "react";

const NAVY = "#0D1B2A";
const NAVY_2 = "#152A40";
const ACCENT = "#5BBCB0";
const ACCENT_DARK = "#3D9B8F";

const T = {
  en: {
    title: "Set a new password",
    sub: "Choose a new password for your account",
    password: "New password",
    confirmPassword: "Confirm password",
    passwordRules: "Min 8 chars, uppercase, lowercase, number, special character",
    submit: "Update password",
    submitting: "Updating...",
    success: "Password updated successfully",
    successSub: "You can now sign in with your new password",
    backToLogin: "Back to sign in",
    errPasswordWeak: "Password must have at least 8 characters, including uppercase, lowercase, number, and special character",
    errPasswordsMatch: "Passwords don't match",
    showPassword: "Show password",
    hidePassword: "Hide password",
    invalidToken: "Invalid or missing token",
  },
  es: {
    title: "Establece una nueva contraseña",
    sub: "Elige una nueva contraseña para tu cuenta",
    password: "Nueva contraseña",
    confirmPassword: "Confirmar contraseña",
    passwordRules: "Mínimo 8 caracteres, mayúscula, minúscula, número, carácter especial",
    submit: "Actualizar contraseña",
    submitting: "Actualizando...",
    success: "Contraseña actualizada exitosamente",
    successSub: "Ya puedes iniciar sesión con tu nueva contraseña",
    backToLogin: "Volver a iniciar sesión",
    errPasswordWeak: "La contraseña debe tener al menos 8 caracteres, incluyendo mayúscula, minúscula, número y carácter especial",
    errPasswordsMatch: "Las contraseñas no coinciden",
    showPassword: "Mostrar contraseña",
    hidePassword: "Ocultar contraseña",
    invalidToken: "Token inválido o faltante",
  },
};

export default function PasswordResetPage({ lang, setLang }) {
  const t = T[lang] || T.es;
  const token = useMemo(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get("token") || "";
    } catch (e) { return ""; }
  }, []);

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  async function handleSubmit() {
    setError("");
    const pwdValid =
      password.length >= 8 &&
      /[A-Z]/.test(password) &&
      /[a-z]/.test(password) &&
      /[0-9]/.test(password) &&
      /[^A-Za-z0-9]/.test(password);
    if (!pwdValid) { setError(t.errPasswordWeak); return; }
    if (password !== confirmPassword) { setError(t.errPasswordsMatch); return; }
    if (!token) { setError(t.invalidToken); return; }

    setSubmitting(true);
    try {
      const res = await fetch("/.netlify/functions/reset-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token: token, password: password }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        setError(data.error || ("HTTP " + res.status));
        setSubmitting(false);
        return;
      }
      setSuccess(true);
      setTimeout(() => { try { window.location.href = "/"; } catch (e) {} }, 3000);
    } catch (e) {
      setError(e.message || String(e));
      setSubmitting(false);
    }
  }

  const inputStyle = {
    width: "100%", padding: "10px 12px",
    background: "rgba(255,255,255,0.06)",
    border: "1px solid rgba(255,255,255,0.15)",
    borderRadius: 8, color: "#fff", fontSize: 13, fontFamily: "inherit",
  };
  const labelStyle = {
    fontSize: 11, fontWeight: 700, color: ACCENT,
    textTransform: "uppercase", letterSpacing: "0.08em",
    marginBottom: 6, display: "block",
  };

  return (
    <div style={{
      minHeight: "100vh",
      background: "linear-gradient(135deg, " + NAVY + " 0%, " + NAVY_2 + " 100%)",
      padding: "40px 20px",
      fontFamily: "'DM Sans', sans-serif",
      color: "#fff",
    }}>
      <div style={{ maxWidth: 480, margin: "0 auto" }}>
        <div style={{ textAlign: "right", marginBottom: 16 }}>
          {["en", "es"].map((l) => (
            <button key={l} onClick={() => setLang(l)} style={{
              padding: "4px 10px", borderRadius: 6,
              border: "1px solid rgba(255,255,255,0.2)",
              background: lang === l ? ACCENT : "transparent",
              color: lang === l ? NAVY : "rgba(255,255,255,0.7)",
              cursor: "pointer", marginLeft: 6, fontSize: 11, fontWeight: 600,
            }}>{l.toUpperCase()}</button>
          ))}
        </div>

        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <svg width="80" height="80" viewBox="-55 -55 110 110">
            <rect x="-35" y="-35" width="70" height="70" rx="16" fill="#1B3A35" />
            <text x="0" y="2" textAnchor="middle" fontFamily="'JetBrains Mono', monospace" fontSize="22" fontWeight="700" fill={ACCENT} letterSpacing="3">GFI</text>
            <rect x="-16" y="10" width="32" height="1.2" rx="0.6" fill={ACCENT_DARK} />
            <text x="0" y="22" textAnchor="middle" fontFamily="'DM Sans', sans-serif" fontSize="8" fontWeight="500" fill={ACCENT_DARK} letterSpacing="3">NEXUS</text>
          </svg>
        </div>

        {success ? (
          <div style={{
            padding: 32, background: "rgba(91,188,176,0.12)",
            border: "1px solid " + ACCENT, borderRadius: 12, textAlign: "center",
          }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: ACCENT, marginBottom: 8 }}>{t.success}</div>
            <div style={{ fontSize: 13, color: "rgba(255,255,255,0.7)" }}>{t.successSub}</div>
          </div>
        ) : (
          <div style={{
            background: "rgba(255,255,255,0.04)",
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: 12, padding: 28,
          }}>
            <h1 style={{ fontSize: 20, fontWeight: 700, margin: "0 0 6px", color: "#fff" }}>{t.title}</h1>
            <div style={{ fontSize: 12, color: "rgba(255,255,255,0.6)", marginBottom: 20 }}>{t.sub}</div>

            <div style={{ marginTop: 12 }}>
              <label style={labelStyle}>{t.password}</label>
              <div style={{ position: "relative" }}>
                <input type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} style={{ ...inputStyle, paddingRight: 40 }} />
                <button type="button" onClick={() => setShowPassword((v) => !v)} title={showPassword ? t.hidePassword : t.showPassword} style={{
                  position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
                  background: "transparent", border: "none", cursor: "pointer",
                  color: "rgba(255,255,255,0.6)", fontSize: 16, padding: 4,
                }}>{showPassword ? "🙈" : "👁"}</button>
              </div>
              <div style={{ fontSize: 10, color: "rgba(255,255,255,0.5)", marginTop: 4 }}>{t.passwordRules}</div>
            </div>

            <div style={{ marginTop: 12 }}>
              <label style={labelStyle}>{t.confirmPassword}</label>
              <div style={{ position: "relative" }}>
                <input type={showConfirm ? "text" : "password"} value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} style={{ ...inputStyle, paddingRight: 40 }} />
                <button type="button" onClick={() => setShowConfirm((v) => !v)} title={showConfirm ? t.hidePassword : t.showPassword} style={{
                  position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
                  background: "transparent", border: "none", cursor: "pointer",
                  color: "rgba(255,255,255,0.6)", fontSize: 16, padding: 4,
                }}>{showConfirm ? "🙈" : "👁"}</button>
              </div>
            </div>

            {error && (
              <div style={{
                marginTop: 16, padding: 10, background: "rgba(231,76,60,0.12)",
                border: "1px solid rgba(231,76,60,0.4)", borderRadius: 8,
                color: "#FCA5A5", fontSize: 12,
              }}>{error}</div>
            )}

            <button onClick={handleSubmit} disabled={submitting} style={{
              marginTop: 20, width: "100%", padding: "12px", borderRadius: 8, border: "none",
              background: submitting ? ACCENT_DARK : ACCENT, color: NAVY,
              fontSize: 14, fontWeight: 700, cursor: submitting ? "not-allowed" : "pointer",
              textTransform: "uppercase", letterSpacing: "0.08em",
            }}>{submitting ? t.submitting : t.submit}</button>
          </div>
        )}
      </div>
    </div>
  );
}
